
import { Component, Inject, Watch, Mixins } from 'vue-property-decorator';
import { GoogleMapConfig } from '@/flightpath-components/types/GoogleMapConfig';
import GoogleMapLoader from '@/components/Map/GoogleMapLoader.vue';
import GoogleMapMachineMarker from '@/components/Map/GoogleMapMachineMarker.vue';
import GoogleMapPolyLine from '@/components/Map/GoogleMapPolyLine.vue';
import GoogleMapPolygon from '@/components/Map/GoogleMapPolygon.vue';
import GoogleMapGroundOverlay from '@/components/Map/GoogleMapGroundOverlay.vue';
import { FlightPathServiceInterface } from '@/flightpath-components/services/FlightPathServiceInterface';
import { PubSub } from '@/PubSubClass';
import { awsConfiguration } from '@/aws-configuration';
import { MqttMessage } from '@/flightpath-components/models/mqttPayload/GPSUpdateMessage';
import { MachineMarker } from '@/flightpath-components/models/mqttPayload/MachineMarkerMessage';
import { google } from 'google-maps';
import { MachineMarkerOptions } from '@/flightpath-components/models/MachineMarkerOptions';
import { SinglePathMission } from '@/flightpath-components/models/SinglePathMission';
import { Overlay } from '@/flightpath-components/models/Overlay';
import { MachineKey } from '@/flightpath-components/models/MachineKey';
import { Container } from 'inversify';
import SERVICE_IDENTIFIER from '@/flightpath-components/services/identifiers';
import MachineStore from '@/store/modules/machine';
import { getModule } from 'vuex-module-decorators';
import GoogleMapRecordedPath from '@/components/Map/GoogleMapRecordedPath.vue';
import MqttMixIn from '@/mixins/mqttManagement';
import MenuStore from '@/store/modules/menu';
import GlobalStore from '@/store/modules/global';
import UpdateStore from '@/store/modules/update';
import { UpdateMachineUpdateIcon } from '@/models/Icons';
import moment from 'moment';

@Component({
  inject: ['container'],
  // Specify `components` option.
  // See Vue.js docs for all available options:
  // https://vuejs.org/v2/api/#Options-Data
  components: {
    GoogleMapLoader,
    GoogleMapMachineMarker,
    GoogleMapPolyLine,
    GoogleMapPolygon,
    GoogleMapGroundOverlay,
    GoogleMapRecordedPath
  }
})
export default class ManualMachineMap extends Mixins(MqttMixIn) {
  apiKey = awsConfiguration.apiKey;

  recordedPolyline: google.maps.PolylineOptions = {
    path: [],
    strokeColor: 'White',
    strokeOpacity: 0.5,
    visible: true
  };

  markers: Array<MachineMarkerOptions> = [];

  polyLines: Array<google.maps.PolylineOptions> = [];

  polygons: Array<google.maps.PolygonOptions> = [];

  overlays: Array<Overlay> = [];

  machineKey: MachineKey = { machineId: 'ded65af1-0ae1-4bb0-8cb6-5a40d4872f19' };

  missionPlan: SinglePathMission | null = null;

  interval: number | undefined = undefined;

  public machineStore = getModule(MachineStore, this.$store);
  public menuStore = getModule(MenuStore, this.$store);
  public globalStore = getModule(GlobalStore, this.$store);
  public updateStore = getModule(UpdateStore, this.$store);
  // @Inject()
  private flightPathService: FlightPathServiceInterface | undefined = undefined;
  // IoC container provided from App.ts is injected here
  @Inject('container')
  private container!: Container;

  _processing = false;

  created(): void {
    this.flightPathService = this.container.get<FlightPathServiceInterface>(SERVICE_IDENTIFIER.FLIGHT_PATH);

    this.addMqttVisibility(this.globalStore.filteredMachineMarkerTopic, () =>
      PubSub.subscribe(this.globalStore.filteredMachineMarkerTopic).subscribe({
        next: (data) => this.markerRecieved(data),
        error: (error) => this.mqttError(error)
      })
    );
  }

  async mounted() {
    if (this.flightPathService != undefined) await this.flightPathService.updateToken();
    this.actions.forEach((act, key) => {
      this.subscriptions.set(key, act());
    });

    this.getMachines();
  }

  destroyed() {
    this.removeMqttVisibility();
  }
  /**
   * Get current active mission plan and render field definition
   */
  private async getMachines() {
    if (this.flightPathService == undefined) return;
    this.menuStore.setLoadingMachines(true);
    try {
      const machineResponse = await this.flightPathService.getAvailableMachines();
      if (machineResponse != null) {
        const json = JSON.stringify(machineResponse.data);
        const availableMachines = JSON.parse(json) as Array<MachineKey>;
        this.machineStore.setAvailableMachines(availableMachines);
        if (this.flightPathService == undefined) return;
        const machineStatusResponse = await this.flightPathService.getMachineStatus(
          availableMachines.map((map) => {
            return map.machineId;
          })
        );
        if (machineStatusResponse != null) {
          const json = JSON.stringify(machineStatusResponse.data);

          const cachedmachines = JSON.parse(json) as Array<MachineMarker>;
          cachedmachines.forEach((element) => {
            if (element.Key.machineId == this.machineStore.selectedMachine?.machineId) this.updatePosition(element);
          });
          const machines = cachedmachines
            .filter((val) => new Date(val.SentUTC).getTime() > moment().subtract(2, 'months').toDate().getTime())
            .map((val) => val.Key);
          this.machineStore.setAvailableMachines(machines);
        }
      }
    } finally {
      this.menuStore.setLoadingMachines(false);
    }
  }

  @Watch('machineStore.selectedMachine')
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onSelectedMachineChange(value: MachineKey): void {
    this.getMachines();
  }

  @Watch('machineStore.isRecordingPlannedPath')
  onRecordingPlannedPathChange(value: boolean): void {
    if (!value) {
      // remove path from map
      this.recordedPolyline = {
        path: []
      };
    }
  }

  private markerRecieved(data: MqttMessage): void {
    this.updatePosition(data.value);
    this.updateRecordedPath(data.value);
  }
  private updatePosition(data: MachineMarker) {
    if (data.Location == null) return;
    const selectedMachine = this.machineStore.selectedMachine;
    const machineUpdateIndex = this.updateStore.machineUpdates.findIndex(
      (o) => o.machine_id.localeCompare(data.Key.machineId, undefined, { sensitivity: 'base' }) == 0
    );
    if (machineUpdateIndex >= 0) {
      const statusUpdate = this.updateStore.machineUpdates[machineUpdateIndex];
      data = UpdateMachineUpdateIcon(data, statusUpdate);
    }
    const marker: MachineMarkerOptions = {
      UpdateMessage: data,
      Machine: data.Key,
      MarkerOptions: {
        position: {
          lat: data.Location.Location.Latitude,
          lng: data.Location.Location.Longitude
        },
        zIndex: selectedMachine != null && selectedMachine.machineId == data.Key.machineId ? 99 : 1,
        title: `Machine: ${data.Key.MachineName ?? data.Key.machineId}`
      }
    };
    const machineKey = (data as MachineMarker).Key;

    const index = this.markers.findIndex((val) => val.Machine.machineId == machineKey.machineId);

    if (index >= 0) {
      this.markers.splice(index, 1, marker);
    } else {
      this.markers.push(marker);
    }
  }

  private updateRecordedPath(data: MachineMarker) {
    if (
      data.Location == null ||
      !this.machineStore.isRecording ||
      this.machineStore.isRecordingPaused ||
      this.machineStore.selectedMachine == null ||
      this.machineStore.selectedMachine?.machineId != data.Key.machineId
    )
      return;

    this.machineStore.pushNewRecordedPathPoint({ loc: data.Location.Location, timestamp: new Date(data.SentUTC) });

    const newPath = this.machineStore.recordedPath.map((x) => {
      return new window.google.maps.LatLng({ lat: x.Latitude, lng: x.Longitude });
    });

    this.recordedPolyline = {
      path: newPath
    };
  }

  get mapConfig(): GoogleMapConfig {
    return {
      locationControl: false,
      loaderOptions: { libraries: ['geometry'] },
      mapOptions: {
        styles: [{ featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }],
        zoom: 17,
        panControl: false,
        rotateControl: false,
        center: this.globalStore.defaultMapLocation,
        mapTypeId: 'hybrid',
        mapTypeControl: false,
        zoomControl: false,
        fullscreenControl: false,
        streetViewControl: false,
        gestureHandling: 'greedy',
        clickableIcons: false,
        disableDefaultUI: true
      }
    };
  }
}
